import { createFileRoute } from '@tanstack/react-router'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { User, Mail, Shield, Trash2, ExternalLink, Loader2, AlertCircle } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { useSubscription } from '@/hooks/use-subscription'
import { Link } from '@tanstack/react-router'

export const Route = createFileRoute('/dashboard/settings/')({ 
  component: SettingsPage
})

function SettingsPage() {
  const [emailThreshold, setEmailThreshold] = useState(90)
  const { user, loading: authLoading } = useAuth()
  const { subscription, loading: subscriptionLoading } = useSubscription()
  
  const loading = authLoading || subscriptionLoading

  const handleUpdateThreshold = async () => {
    // TODO: Implement API call to update email threshold
    console.log('Updating email threshold to:', emailThreshold)
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading settings...</span>
          </div>
        </div>
      </ProtectedRoute>
    )
  }

  if (!user) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to load user data</h3>
              <p className="text-gray-500">Please try refreshing the page</p>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    )
  }

  const currentPlan = subscription?.plan ? {
    name: subscription.plan.name,
    credits: 0, // TODO: Get actual usage from usage API
    totalCredits: subscription.plan.credits_included || 1000
  } : {
    name: 'Free',
    credits: 0,
    totalCredits: 1000
  }

  return (
    <ProtectedRoute>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
        </div>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>
              Update your account details and preferences
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                {user.user_metadata?.avatar_url ? (
                  <img 
                    src={user.user_metadata.avatar_url} 
                    alt="Profile" 
                    className="w-16 h-16 rounded-full"
                  />
                ) : (
                  <User className="w-8 h-8 text-blue-600" />
                )}
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-medium">
                  {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                </h3>
                <p className="text-gray-500">{user.email}</p>
                <Badge variant="secondary">{currentPlan.name}</Badge>
              </div>
            </div>
            
            <Separator />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="account-name">Account name</Label>
                <Input 
                  id="account-name" 
                  value={user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'} 
                  readOnly 
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="associated-email">Associated email</Label>
              <Input id="associated-email" value={user.email || ''} readOnly />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-id">User ID</Label>
              <Input id="user-id" value={user.id} readOnly className="font-mono text-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Email Usage Alerts */}
        <Card>
          <CardHeader>
            <CardTitle>Email usage alerts</CardTitle>
            <CardDescription>
              An alert will be sent to your email when your monthly usage reaches the set threshold.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="space-y-2 flex-1">
                <Label htmlFor="alert-threshold">Your alert threshold is currently set to:</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="alert-threshold"
                    type="number"
                    value={emailThreshold}
                    onChange={(e) => setEmailThreshold(Number(e.target.value))}
                    className="w-20"
                    min="0"
                    max="100"
                  />
                  <span className="text-sm text-gray-500">%</span>
                  <Button variant="outline" size="sm" onClick={handleUpdateThreshold}>
                    Update
                  </Button>
                </div>
              </div>
              <div className="text-right">
                <Badge variant="secondary">Enabled</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Account Actions</CardTitle>
            <CardDescription>
              Manage your account and data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h4 className="font-medium">Export Data</h4>
                <p className="text-sm text-gray-500">Download all your account data</p>
              </div>
              <Button variant="outline" disabled>
                <ExternalLink className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
            
            <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
              <div>
                <h4 className="font-medium text-red-900">Delete Account</h4>
                <p className="text-sm text-red-600">Permanently delete your account and all data</p>
              </div>
              <Button variant="destructive" disabled>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </ProtectedRoute>
  )
}